/**
 * Public /dialer route.
 *
 * If the user is logged in, redirect them straight into the in-app dialer at
 * /app/dial. Otherwise show the same `CallingDialer` in *guest preview* mode —
 * the keypad and country picker are fully usable and pressing Call sends them
 * to /register with the number stashed so the flow continues after sign-up.
 *
 * This is the page the "Start Calling" home-page CTA, the nav menu and the
 * floating call FAB all point to.
 */
import { Helmet } from "react-helmet-async";
import { Link, Navigate } from "react-router-dom";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import CallingDialer from "@/components/customer/CallingDialer";
import SEO from "@/components/site/SEO";
import { ShieldCheck, Zap, Wallet } from "lucide-react";

export default function PublicDialer() {
  const { user, loading } = useCustomerAuth() || {};

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to="/app/dial" replace />;

  return (
    <div className="min-h-screen bg-brand-bgSoft py-10 sm:py-16" data-testid="public-dialer-page">
      <SEO title="Dialer · Call Africa from anywhere"
           description="Try the OneDial Africa dialer. Crystal-clear calls, per-second billing, no hidden fees."
           path="/dialer" />
      <Helmet><meta name="theme-color" content="#0c2a4d" /></Helmet>

      <div className="container-page max-w-3xl">
        <div className="text-center mb-8">
          <p className="text-[11px] uppercase tracking-[0.24em] font-bold text-brand-greenDark mb-3">Try the dialer</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl leading-tight text-brand-ink">
            Call Africa from anywhere in the world.
          </h1>
          <p className="mt-3 text-sm sm:text-base text-brand-muted max-w-xl mx-auto">
            Crystal-clear calls. Per-second billing. No hidden fees. Tap a number, choose a country, then sign up to place your call.
          </p>
        </div>

        <CallingDialer guestPreview />

        <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-md sm:max-w-2xl mx-auto">
          <div className="rounded-2xl bg-white border border-brand-line p-4 flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-brand-greenDark shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-brand-ink">UK-regulated</p>
              <p className="text-[11px] text-brand-muted leading-snug">Scitygate Telecom UK Ltd · Co. No. 16468736</p>
            </div>
          </div>
          <div className="rounded-2xl bg-white border border-brand-line p-4 flex items-start gap-3">
            <Zap className="h-5 w-5 text-brand-greenDark shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-brand-ink">Per-second billing</p>
              <p className="text-[11px] text-brand-muted leading-snug">Pay only for what you talk. No 60-second rounding.</p>
            </div>
          </div>
          <div className="rounded-2xl bg-white border border-brand-line p-4 flex items-start gap-3">
            <Wallet className="h-5 w-5 text-brand-greenDark shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-brand-ink">GBP wallet</p>
              <p className="text-[11px] text-brand-muted leading-snug">Top up by card. Closed-loop service credit.</p>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/register" data-testid="public-dialer-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-green hover:bg-brand-greenHover text-white px-6 py-3 text-sm font-bold">
            Sign up free — £1 free credit
          </Link>
          <p className="mt-2 text-[11px] text-brand-muted">
            Already on OneDial? <Link to="/login" className="font-semibold text-brand-greenDark hover:underline">Sign in →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
